import React, { useEffect, useRef, useState } from 'react';
import { LogOut, Settings, User as UserIcon } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/LanguageContext';
import SettingsModal from './SettingsModal';

const initialsOf = (user) =>
  (user?.name || user?.email || 'U')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Avatar button + popover (profile, Settings, Sign out) — same items as the
 *  mobile ProfileMenu. Settings opens as a modal. */
export default function UserMenu() {
  const { user, isConfigured, login, logout } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isConfigured && !user) {
    return (
      <button
        type="button"
        onClick={login}
        className="px-3 h-10 rounded-full bg-keep-accent text-keep-onAccent text-[13px] font-medium hover:opacity-90"
        data-testid="user-menu-login"
      >
        {t('profileMenu.signIn')}
      </button>
    );
  }

  // Stub mode (no Cognito env) runs as dev-user — still gets the menu so
  // Settings is reachable, just without Sign out.
  const isStub = !isConfigured;

  return (
    <div ref={ref} className="relative">
      {isStub ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-3 h-10 rounded-full bg-keep-searchBg text-xs text-keep-textMuted hover:bg-keep-hover"
          title={t('profileMenu.stubHint')}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="user-menu-stub"
        >
          <UserIcon className="w-4 h-4" />
          <span className="hidden md:inline">dev-user</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-9 h-9 rounded-full bg-keep-accent text-keep-onAccent text-xs font-semibold flex items-center justify-center overflow-hidden hover:opacity-90"
          title={user.email || user.name}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="user-menu-button"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            initialsOf(user)
          )}
        </button>
      )}

      {open && (
        <div
          role="menu"
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute right-0 mt-2 w-64 bg-keep-surface rounded-xl shadow-keepHover border border-keep-border overflow-hidden z-50 animate-fade-in"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-keep-border flex items-center gap-3">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-keep-accent text-keep-onAccent text-xs font-semibold flex items-center justify-center flex-shrink-0">
                {isStub ? <UserIcon className="w-4 h-4" /> : initialsOf(user)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-keep-text truncate">
                {isStub ? 'dev-user' : user.name || t('profileMenu.account')}
              </div>
              <div className="text-xs text-keep-textMuted truncate">
                {isStub ? t('profileMenu.stubHint') : user.email}
              </div>
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setSettingsOpen(true);
            }}
            className="w-full px-4 py-3 text-left text-sm text-keep-text hover:bg-keep-hover flex items-center gap-3"
            data-testid="user-menu-settings"
          >
            <Settings className="w-[18px] h-[18px] text-keep-textMuted" />
            {t('profileMenu.settings')}
          </button>
          {!isStub && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="w-full px-4 py-3 text-left text-sm text-keep-text hover:bg-keep-hover flex items-center gap-3"
              data-testid="user-menu-logout"
            >
              <LogOut className="w-[18px] h-[18px] text-keep-textMuted" />
              {t('profileMenu.signOut')}
            </button>
          )}
        </div>
      )}

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
